import { DatePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import {
    AbstractControl,
    FormBuilder,
    FormControl,
    FormGroup,
    ValidationErrors,
    ValidatorFn,
} from '@angular/forms';
import { forkJoin } from 'rxjs';
import { Role } from 'src/app/enums/role';
import { Pagination } from 'src/app/model/Pagination';
import { User } from 'src/app/model/user';
import { CommonService } from 'src/app/services/common.service';
import { CommonUtil } from 'src/app/services/common.util';
import { ExportService } from 'src/app/services/export.service';
import { StorageService } from 'src/app/services/storage.service';
import { MessageConstant } from 'src/app/settings/message.constant';
import { StorageConstant } from 'src/app/settings/storage-constants';

interface ProvinceOption {
    id: number | string;
    province: string;
}

@Component({
    selector: 'app-search',
    templateUrl: './search.component.html',
    styleUrls: ['./search.component.css'],
})
export class SearchComponent implements OnInit {
    accountTypeDropdown: string[] = [
        Role.personal,
        Role.business,
        Role.staff,
        Role.agent,
        Role.enterprise,
    ];
    statusDropdown: string[] = [];
    idTypeDropdown: string[] = [];
    provinceList: ProvinceOption[] = [];
    form: FormGroup;
    invalidForm = false;
    userList: User[] = [];
    excelList: Record<string, unknown>[] = [];
    pagination = new Pagination<User>();
    showOptions = false;
    search = false;
    searchItems: string[] = [];
    itemList = ['businessName', 'role', 'status', 'idType', 'idNumber', 'suburb', 'referredBy'];
    mobileChanged = false;
    userCreated = false;
    createdMessage = '';


    constructor(
        private formBuilder: FormBuilder,
        private commonService: CommonService,
        private storageService: StorageService,
        public commonUtil: CommonUtil,
        private datePipe: DatePipe,
        private exportService: ExportService,
    ) {
        this.storageService.initializeProfileUser();
        const item = localStorage.getItem(StorageConstant.CHANGE_MOBILE_SUCCESS);
        localStorage.removeItem(StorageConstant.CHANGE_MOBILE_SUCCESS);

        if (item && item === 'success') {
            this.mobileChanged = true;
        }

        if (localStorage.getItem(StorageConstant.STAFF_CREATED_MESSAGE)) {
            localStorage.removeItem(StorageConstant.STAFF_CREATED_MESSAGE);
            this.createdMessage = MessageConstant.STAFF_CREATED;
            this.userCreated = true;
        } else if (localStorage.getItem(StorageConstant.AGENT_CREATED_MESSAGE)) {
            localStorage.removeItem(StorageConstant.AGENT_CREATED_MESSAGE);
            this.createdMessage = MessageConstant.AGENT_CREATED;
            this.userCreated = true;
        } else if (localStorage.getItem(StorageConstant.ENTERPRISE_CREATED_MESSAGE)) {
            localStorage.removeItem(StorageConstant.ENTERPRISE_CREATED_MESSAGE);
            this.createdMessage = MessageConstant.ENTERPRISE_CREATED;
            this.userCreated = true;
        }

        this.form = this.formBuilder.group({
            text: [''],
            businessName: [''],
            role: [''],
            idNumber: [''],
            idType: [''],
            status: [''],
            start: [''],
            length: [''],
            fromDate: new FormControl<Date | null>(null),
            toDate: new FormControl<Date | null>(null),
            suburb: [''],
            referredBy: [''],
            provinceId: [null],
        }, {
            validators: [this.optionalDateRangeValidator()],
        });
    }

    optionalDateRangeValidator(): ValidatorFn {
        return (group: AbstractControl): ValidationErrors | null => {
            const from = group.get('fromDate')?.value;
            const to = group.get('toDate')?.value;

            if (from && to && from > to) {
                return { invalidDateRange: true };
            }
            return null;
        };
    }

    ngOnInit(): void {
        this.getData();

        forkJoin({
            userStatus: this.commonService.getUserStatus(),
            idType: this.commonService.getIdType(),
            provinces: this.commonService.getProvinces({ allowedForShop: true }),
            accountType: this.commonService.getAccountType(),
        }).subscribe({
            next: ({ userStatus, idType, provinces, accountType }) => {
                this.statusDropdown = userStatus.data;
                this.idTypeDropdown = idType.data;
                this.provinceList = provinces;
                this.accountTypeDropdown = accountType.data;
            },
        });
    }

    getData(start = 0, length = 0, download = false): void {
        if (download) {
            this.excelList = [];
        } else {
            length = this.pagination.length;
            start = this.pagination.start;
        }

        this.search = false;
        this.form.patchValue({ start, length });
        const requestForm = this.buildRequestForm(start, length);

        this.commonService.getFilteredData(requestForm).subscribe({
            next: (res) => {
                setTimeout(() => {
                    if (res.data && download) {
                        for (const user of res.data) {
                            this.excelList.push(this.commonUtil.getExcelOfUsers(user));
                        }
                        this.exportService.exportExcel(this.excelList, 'User List');
                    } else {
                        this.userList = res.data?.map((item: Partial<User>) => new User(item)) || [];
                        this.pagination.total = res.total ?? 0;
                    }
                    this.search = true;
                }, 350);

                this.searchItems = [];
                for (const item of this.itemList) {
                    const control = this.form.get(item);
                    if (control?.value) {
                        this.searchItems.push(control.value);
                    }
                }
            },
            error: () => {
            },
        });
    }

    clearFilters(): void {
        this.form.patchValue({
            text: '',
            businessName: '',
            role: '',
            idNumber: '',
            idType: '',
            status: '',
            start: '',
            length: '',
            fromDate: null,
            toDate: null,
            suburb: '',
            referredBy: '',
            provinceId: null,
        });

        setTimeout(() => {
            this.getData();
        }, 500);
    }

    searchUsers(text: string): void {
        this.pagination.text = text;
        this.getData();
    }

    removeSearchItem(value: string): void {
        for (const item of this.itemList) {
            const control = this.form.get(item);
            if (control?.value === value) {
                control.setValue('');
                this.getData();
                break;
            }
        }
    }

    clearText(): void {
        this.form.get('text')?.setValue('');
    }

    closeModel(_: Event | boolean): void {
        this.mobileChanged = false;
        this.userCreated = false;
    }

    receiveFilter(filter: { name: string; value: number }): void {
        // #TODO - implement filter functionality
        // const control = this.form.get(filter.name);
        // if (control) {
        //     control.setValue(filter.value);
        //     this.getData();
        // }
    }

    changeLength(len: number): void {
        this.pagination.length = len;
        this.pagination.start = 0;
        this.getData();
    }

    changePage(page: number): void {
        this.pagination.changePage(page);
        this.getData();
    }

    get controls(): any {
        return this.form.controls;
    }

    toggleOption(): void {
        this.showOptions = !this.showOptions;
    }

    clearDates(): void {
        this.form.patchValue({
            fromDate: null,
            toDate: null,
        });
    }

    private buildRequestForm(start: number, length: number): FormGroup {
        const rawValue = this.form.getRawValue();

        return this.formBuilder.group({
            ...rawValue,
            start,
            length,
            fromDate: this.formatFromDate(rawValue.fromDate),
            toDate: this.formatToDate(rawValue.toDate),
        });
    }

    private formatFromDate(value: Date | null): string {
        if (!value) {
            return '';
        }

        return this.datePipe.transform(value, 'yyyy-MM-dd') ?? '';
    }

    private formatToDate(value: Date | null): string {
        if (!value) {
            return '';
        }

        const newDate = new Date(value);
        newDate.setDate(newDate.getDate() + 1);

        return this.datePipe.transform(newDate, 'yyyy-MM-dd') ?? '';
    }

    submit(): void {
        this.getData();
    }

    downLoadExcel(): void {
        this.getData(0, 10000, true);
    }
}
